import { useEffect, useRef, useState, type ReactNode } from "react"
import { Phone, Star } from "lucide-react"
import { toast } from "sonner"
import type { CoachPrompt } from "@/lib/coach-prompts"

import { Button } from "@/components/ui/button"
import { CoachThread, type CoachThreadFrame } from "@/components/coach-thread"
import {
  VoiceSessionOverlay,
  VoiceSessionPanel,
  type VoiceTarget,
} from "@/components/voice-session-overlay"
import {
  useCoachThread,
  useCreateCoachThread,
  useRefreshCoachThreads,
  useUpdateCoachThread,
} from "@/lib/queries"
import { useCoach } from "@/lib/use-coach"
import {
  COACH_FALLBACK_LABEL,
  type ChatMode,
  type GespraechsPhase,
} from "@nl/shared"

/**
 * One conversation with the coach: either he answers questions, or, in training
 * mode, he asks them.
 *
 * Without a threadId this is a conversation that does not exist yet. It is
 * created on the first message, not on mount, so opening the chat and leaving
 * again does not litter the rail with empty entries.
 *
 * The two modes live on two pages now, so the page owns the routing: it says
 * where a freshly created thread has to appear in the URL and where "start
 * over" leads. Hard-coding `/chat` here would send the product training back to
 * the open exchange.
 */
export function GeneralCoachChat({
  threadId,
  initialPrompt,
  onInitialPromptTaken,
  mode,
  trainingPhase,
  produkt,
  extraBody,
  onThreadCreated,
  onReset,
  onFinish,
  aboveThread,
  empty,
  placeholder,
  frame,
  barSize,
  autoFocus,
  autoStartVoice = false,
}: {
  threadId?: string
  initialPrompt?: string
  onInitialPromptTaken?: () => void
  mode: ChatMode
  /** Picks the question bank; only read in training mode. */
  trainingPhase?: GespraechsPhase
  /** Unlocks the product knowledge; only read in training mode. */
  produkt?: string
  /**
   * Weitere Felder im Anfragekörper, je Zug frisch ausgewertet.
   *
   * Für alles, was diese Komponente nicht kennt — etwa die Kennung der gerade
   * bewerteten Prüfungsantwort, die eine Karte im Verlauf setzt. Wird nach den
   * eigenen Feldern gemischt.
   */
  extraBody?: () => Record<string, unknown>
  onThreadCreated: (threadId: string) => void
  onReset: () => void
  /** After each completed coach answer, e.g. to refetch state a tool changed. */
  onFinish?: () => void
  /** Scrolls with the thread, above it — the onboarding hero block. */
  aboveThread?: ReactNode
  empty: {
    title: string
    hint?: string
    prompts: CoachPrompt[]
    actions?: ReactNode
    intro?: ReactNode
  }
  placeholder: string
  /**
   * Own layout around thread and composer; see `CoachThread`. The call is
   * handed along because the thread has to exist before it can start, and the
   * running call comes back as `voice` so the layout can put it on its stage
   * instead of in a dialog.
   */
  frame?: (
    parts: CoachThreadFrame & { startCall: () => void; voice: ReactNode | null }
  ) => ReactNode
  barSize?: "sm" | "md"
  autoFocus?: boolean
  autoStartVoice?: boolean
}) {
  const coach = useCoach()
  const coachName = coach?.name ?? COACH_FALLBACK_LABEL

  const {
    data: thread,
    isLoading: threadLoading,
    isFetching: threadFetching,
    isError: threadFailed,
    refetch: refreshThread,
  } = useCoachThread(threadId)
  // Which conversation has been seeded on screen. `useChat` copies the thread
  // into its own store when the conversation mounts and ignores later data,
  // so the first mount must wait for the fetch the query starts on mount
  // instead of seeding from a cached copy. Resuming a conversation from the
  // start page used to show it empty for exactly that reason. Once seeded, a
  // refetch must not unmount it — that would cancel a running answer.
  const seededFor = useRef<string | undefined>(undefined)
  const createThread = useCreateCoachThread()
  const refreshThreads = useRefreshCoachThreads()
  const updateThread = useUpdateCoachThread()
  // Holds the id created for this conversation until the URL catches up, so a
  // second message cannot open a second thread.
  const liveThreadId = useRef(threadId)

  // True while this conversation is the one we just opened ourselves. Its
  // content is on screen already, so there is nothing to wait for — and waiting
  // would unmount the thread mid-send and drop the very message that created it.
  const selfCreated = useRef(false)

  // Start over whenever the id in the URL stops matching the conversation on
  // screen: another one was picked from the rail, or a new one was begun. The
  // step from "none yet" to the id we just created is not such a change —
  // `ensureThreadId` has already written it here.
  const [epoch, setEpoch] = useState(0)
  useEffect(() => {
    if (threadId === liveThreadId.current) return
    liveThreadId.current = threadId
    selfCreated.current = false
    setEpoch((n) => n + 1)
  }, [threadId])

  const [voiceOpen, setVoiceOpen] = useState(false)
  const autoVoiceStarted = useRef(false)
  useEffect(() => {
    if (
      !autoStartVoice ||
      autoVoiceStarted.current ||
      !threadId ||
      threadLoading ||
      threadFailed
    )
      return
    autoVoiceStarted.current = true
    setVoiceOpen(true)
  }, [autoStartVoice, threadId, threadLoading, threadFailed])

  async function ensureThreadId(): Promise<string | undefined> {
    if (liveThreadId.current) return liveThreadId.current
    try {
      const created = await createThread.mutateAsync({ body: {} })
      liveThreadId.current = created.id
      selfCreated.current = true
      onThreadCreated(created.id)
      return created.id
    } catch {
      // Without a thread the turn has nowhere to be stored, so say so instead
      // of letting the message vanish silently.
      toast.error("Das Gespräch konnte nicht angelegt werden.")
      throw new Error("coach thread creation failed")
    }
  }

  // Waiting is only right when the content still has to arrive. Unmounting a
  // conversation that is already on screen would cancel a running answer.
  const seeding = !!threadId && !selfCreated.current && seededFor.current !== threadId
  if (seeding && (threadLoading || threadFetching)) return null

  // A stale id from the URL or from a conversation deleted elsewhere: rendering
  // the thread anyway would send into a 404 and swallow the advisor's message.
  if (threadId && !selfCreated.current && threadFailed) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <p className="text-[14px] text-muted-foreground">
          Dieses Gespräch gibt es nicht mehr.
        </p>
        <Button variant="outline" size="sm" onClick={onReset}>
          Neues Gespräch
        </Button>
      </div>
    )
  }

  // The thread has to exist before the call: audio runs peer to peer, and the
  // turns are reported to a thread id afterwards. Without one the whole call
  // would be lost on hanging up.
  async function startCall() {
    try {
      await ensureThreadId()
    } catch {
      return
    }
    setVoiceOpen(true)
  }

  const voiceTarget: VoiceTarget = {
    kind: "coach-thread",
    threadId: liveThreadId.current,
    coach,
    chatMode: mode,
    trainingPhase: mode === "training" ? trainingPhase : undefined,
    produkt: mode === "training" ? produkt : undefined,
  }

  // The call is stored server-side; refetching is what brings it on screen.
  // Remount only after the turns are in: useChat seeds on mount, so a
  // premature epoch bump would replay the thread without the call.
  const afterCall = async () => {
    await refreshThread()
    void refreshThreads()
    setEpoch((n) => n + 1)
    // A call is a completed exchange too — the onboarding page refetches the
    // essence here, because the voice pipeline may just have completed it.
    onFinish?.()
  }

  if (threadId && thread) seededFor.current = threadId

  const voice =
    frame && voiceOpen ? (
      <VoiceSessionPanel
        target={voiceTarget}
        onEnd={(turns) => {
          setVoiceOpen(false)
          if (turns.length > 0) void afterCall()
        }}
      />
    ) : null

  return (
    <>
      <CoachThread
        key={epoch}
        scope={{ kind: "open" }}
        initialMessages={thread?.messages}
        initialPrompt={initialPrompt}
        onInitialPromptTaken={onInitialPromptTaken}
        // The title is written with the first answer, so the rail can only show
        // the conversation once the turn is through.
        onFinish={() => {
          void refreshThreads()
          onFinish?.()
        }}
        scroll="inner"
        mode={mode}
        aboveThread={aboveThread}
        beforeSend={ensureThreadId}
        extraBody={() => ({
          threadId: liveThreadId.current,
          mode,
          trainingPhase: mode === "training" ? trainingPhase : undefined,
          produkt: mode === "training" ? produkt : undefined,
          ...extraBody?.(),
        })}
        empty={{
          hero: {
            portraitUrl: coach?.portraitUrl,
            name: coachName,
            role: COACH_FALLBACK_LABEL,
          },
          ...empty,
        }}
        placeholder={{ ready: placeholder }}
        barSize={barSize}
        autoFocus={autoFocus}
        frame={
          frame
            ? (parts) =>
                frame({ ...parts, startCall: () => void startCall(), voice })
            : undefined
        }
        composerAction={({ isEmpty }) =>
          // The stage offers the call as a labelled button next to the input;
          // a second, unlabelled one inside it would only raise the question
          // which of the two is which.
          frame && isEmpty ? null : (
            <>
              {/* Only a stored conversation can be favorited; before the first
                answer there is nothing to find again. */}
              {thread && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() =>
                    void updateThread
                      .mutateAsync({
                        path: { id: thread.id },
                        body: { favorite: !thread.favorite },
                      })
                      .catch(() =>
                        toast.error(
                          "Konnte nicht gespeichert werden. Bitte versuche es erneut."
                        )
                      )
                  }
                  aria-label={
                    thread.favorite
                      ? "Gespräch aus Favoriten entfernen"
                      : "Gespräch zu Favoriten hinzufügen"
                  }
                  aria-pressed={thread.favorite}
                  title={
                    thread.favorite
                      ? "Aus Favoriten entfernen"
                      : "Zu Favoriten hinzufügen"
                  }
                  className="shrink-0 rounded-md text-muted-foreground hover:text-foreground"
                >
                  <Star
                    className={
                      thread.favorite
                        ? "h-4 w-4 fill-current text-amber-500"
                        : "h-4 w-4"
                    }
                  />
                </Button>
              )}
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => void startCall()}
                aria-label={`${coachName} anrufen`}
                title={`${coachName} anrufen`}
                className="shrink-0 rounded-md text-muted-foreground hover:text-foreground"
              >
                <Phone className="h-4 w-4" />
              </Button>
            </>
          )
        }
      />

      {/* Without a frame the call still opens as a dialog over the chat. */}
      {!frame && (
        <VoiceSessionOverlay
          open={voiceOpen}
          onOpenChange={setVoiceOpen}
          target={voiceTarget}
          onTranscriptComplete={() => void afterCall()}
        />
      )}
    </>
  )
}
